import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import GoogleSignInButton from './GoogleSignInButton';
import { useAccount } from '../context/AccountContext';
import styles from '../styles/AuthModal.module.css';

const AuthModal = ({ onClose }) => {
    const { user, login, register } = useAccount();
    const [mode, setMode] = useState('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // El boton de Google actualiza la sesion por su cuenta (su propio
    // callback llama a refresh()); en cuanto el contexto refleje un usuario
    // logueado, cerramos el modal aunque no haya pasado por el formulario.
    useEffect(() => {
        if (user) {
            onClose();
        }
    }, [user, onClose]);

    // Sin esto, la pagina de detras se desplaza a la vez que se hace scroll
    // dentro del modal (el fondo no bloquea el scroll solo porque este encima).
    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setIsSubmitting(true);

        try {
            if (mode === 'login') {
                await login(email, password);
            } else {
                await register(email, password, displayName);
            }
            onClose();
        } catch (submitError) {
            setError(submitError.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Portal directo a document.body: el header tiene backdrop-filter, y un
    // ancestro con filter/backdrop-filter/transform se convierte en el
    // "containing block" de sus descendientes position:fixed (esto lo aplica
    // Safari/WebKit de forma mas estricta que Chrome de escritorio). Sin el
    // portal, el modal quedaba encajonado dentro de la franja del header en
    // vez de cubrir toda la pantalla.
    return createPortal(
        <div className={styles.backdrop} onClick={onClose}>
            <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
                <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Cerrar">
                    &times;
                </button>

                <div className={styles.tabs}>
                    <button
                        type="button"
                        className={mode === 'login' ? styles.tabActive : styles.tab}
                        onClick={() => { setMode('login'); setError(''); }}
                    >
                        Iniciar sesion
                    </button>
                    <button
                        type="button"
                        className={mode === 'register' ? styles.tabActive : styles.tab}
                        onClick={() => { setMode('register'); setError(''); }}
                    >
                        Crear cuenta
                    </button>
                </div>

                <div className={styles.googleRow}>
                    <GoogleSignInButton />
                </div>

                <div className={styles.divider}><span>o con email</span></div>

                <form onSubmit={handleSubmit} className={styles.form}>
                    {mode === 'register' && (
                        <label>
                            <span>Nombre</span>
                            <input
                                type="text"
                                value={displayName}
                                onChange={(event) => setDisplayName(event.target.value)}
                                placeholder="Como quieres que te llamemos"
                                required
                            />
                        </label>
                    )}
                    <label>
                        <span>Email</span>
                        <input
                            type="email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            required
                        />
                    </label>
                    <label>
                        <span>Contrasena</span>
                        <input
                            type="password"
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            minLength={8}
                            required
                        />
                    </label>

                    {error && <p className={styles.error}>{error}</p>}

                    <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
                        {isSubmitting ? 'Un momento...' : (mode === 'login' ? 'Entrar' : 'Crear cuenta')}
                    </button>
                </form>
            </div>
        </div>,
        document.body
    );
};

export default AuthModal;
